import { Link } from "react-router-dom";
import Seo from "../lib/Seo";
import useReveal from "../lib/useReveal";
import { JoinForm } from "../components/JoinForm";
import { Arrow } from "../components/Icons";
import { JOTFORM_URL } from "../lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1505794718076-13e166c01a33?crop=entropy&cs=srgb&fm=jpg&q=80&w=1920";
const STORY_IMG = "https://images.unsplash.com/photo-1636315662384-8406112deaf1?crop=entropy&cs=srgb&fm=jpg&q=80&w=1920";
const CLOSE_IMG = "https://images.unsplash.com/photo-1500932334442-8761ee4810a7?crop=entropy&cs=srgb&fm=jpg&q=80&w=1920";

const MOMENTS = [
  { key: "prepare", name: "Prepare", color: "var(--c-prepare)", when: "Before the round.", copy: "For the stress, noise and weight that arrived before you did." },
  { key: "reset", name: "Reset", color: "var(--c-reset)", when: "After the shot you cannot stop replaying.", copy: "For the moment one mistake threatens to become three." },
  { key: "sustain", name: "Sustain", color: "var(--c-sustain)", when: "When the round starts asking more of you.", copy: "For the final holes, when energy, attention and patience begin to fade." },
  { key: "recover", name: "Recover", color: "var(--c-recover)", when: "When the scorecard is signed, but your system is still switched on.", copy: "For winding down, resetting and being ready to go again." },
];

const QUOTES = [
  { q: "Stress from work. Stress from being a husband, provider and dad. If I can get a round in once a week, it just makes my life a bit easier.", src: "From the research" },
  { q: "I felt nothing. No aggression, no sadness and weirdly no happiness. It was just nothing, which actually felt amazing.", src: "On the best round of their life" },
  { q: "I wasn't thinking much. I didn't worry about my score. I was just enjoying the view.", src: "From the research" },
];

export default function Home() {
  useReveal();
  return (
    <>
      <Seo
        title="Dialed In | Products for the Person Carrying the Bag"
        description="Dialed In is a golf wellbeing brand building Prepare, Reset, Sustain and Recover around the moments that shape the round."
      />

      {/* HERO */}
      <section className="hero" data-testid="hero-section">
        <div className="hero-bg" data-plx="0.2" style={{ backgroundImage: `url('${HERO_IMG}')` }} />
        <div className="hero-veil" />
        <div className="hero-inner">
          <div className="hero-eyebrow">A golf wellbeing brand</div>
          <h1 className="disp hero-h" data-testid="hero-headline">
            <span className="ln"><span>You waited</span></span>
            <span className="ln"><span>all week</span></span>
            <span className="ln"><span className="g">for this.</span></span>
          </h1>
          <p className="hero-sub">
            By the time you reach the first tee, work is still in your head, your phone is still going, and part of you is already thinking about the score.
          </p>
          <p className="hero-pivot">You're at the golf course. But you're not yet there.</p>
          <div className="hero-actions">
            <Link className="cta" to="/#join" data-testid="hero-join-cta">Join Dialed In <Arrow /></Link>
            <a className="cta cta-ghost" href={JOTFORM_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-research-cta">
              Take Part in the Research
            </a>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <div className="ticker" aria-hidden="true" data-testid="brand-ticker">
        <div className="ticker-track">
          {[0, 1].map((set) => (
            <div className="ticker-set" key={set}>
              {[0, 1].map((rep) => (
                <span key={rep}>
                  <span className="ticker-item">Prepare<i /></span>
                  <span className="ticker-item">Reset<i /></span>
                  <span className="ticker-item">Sustain<i /></span>
                  <span className="ticker-item">Recover<i /></span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* DIALED IN */}
      <section className="sec" data-testid="section-dialed-in">
        <div className="wrap">
          <div className="label rv rv-up"><span className="idx">01</span>&nbsp;Dialed In</div>
          <h2 className="disp sec-h rv rv-up">Golf gives people something <span className="g">worth protecting.</span></h2>
          <div className="body-copy rv rv-up" style={{ maxWidth: 640, marginTop: "4vh" }}>
            <p>Not just the score, but the escape, the connection, the walk, the competition, the few hours where the rest of life gets quieter.</p>
            <p>Dialed In is building products for the person carrying the bag, not another golf supplement brand shouting about peak performance.</p>
            <p><strong>A golf wellbeing brand built around what actually affects the round long before the swing begins.</strong></p>
          </div>
        </div>
      </section>

      {/* MOST GOLFERS DO NOT ARRIVE EMPTY */}
      <section className="sec sec-2" data-testid="section-arrive">
        <div className="wrap">
          <div className="label rv rv-up"><span className="idx">02</span>&nbsp;What golfers carry</div>
          <h2 className="disp sec-h rv rv-up">Most golfers don't <span className="g">arrive empty.</span></h2>
          <div className="body-copy rv rv-up" style={{ maxWidth: 640, marginTop: "4vh" }}>
            <p>They arrive carrying work, family, responsibility, pressure, tiredness, the argument from earlier, the score they think they should shoot, the person they don't want to embarrass themselves in front of.</p>
            <p>Then golf asks them to stand over a ball and be completely present.</p>
          </div>
          <div className="verse rv rv-up" style={{ marginTop: "5vh" }}>
            <p>Sometimes that happens on the first tee.</p>
            <p>Sometimes it takes three holes.</p>
            <p><strong>Sometimes the round ends before they ever really settle into it.</strong></p>
          </div>
          <p className="serif-line rv rv-up" style={{ marginTop: "5vh", fontSize: "clamp(20px, 2.6vw, 27px)" }}>
            That's the space Dialed In is being built for.
          </p>
        </div>
      </section>

      {/* WE STARTED BY LISTENING */}
      <section className="sec" data-testid="section-listening">
        <div className="wrap">
          <div className="label rv rv-up"><span className="idx">03</span>&nbsp;The research</div>
          <h2 className="disp sec-h rv rv-up">We started by <span className="g">listening.</span></h2>
          <div className="body-copy rv rv-up" style={{ maxWidth: 640, marginTop: "4vh" }}>
            <p>Not asking golfers what flavour gummy they want, or whether they would buy another supplement.</p>
            <p><strong>But why they play.</strong> What they arrive carrying. What their best rounds feel like. What happens when the game starts slipping away. And what helps them feel like themselves again.</p>
          </div>

          <div className="quotes">
            {QUOTES.map((item, i) => (
              <div className={`quote-card rv rv-up`} key={i} data-testid={`research-quote-${i + 1}`}>
                <p>&ldquo;{item.q}&rdquo;</p>
                <div className="src">{item.src}</div>
              </div>
            ))}
          </div>

          <div className="verse rv rv-up">
            <p>Different golfers. Different lives. The same thing keeps showing up.</p>
            <p><strong>When the noise drops, golf feels different. So does the person playing it.</strong></p>
          </div>
          <div className="rv rv-up" style={{ marginTop: "5vh" }}>
            <a className="cta cta-ghost" href={JOTFORM_URL} target="_blank" rel="noopener noreferrer" data-testid="listening-research-cta">
              Share Your Golf Experience <Arrow />
            </a>
          </div>
        </div>
      </section>

      {/* PREPARE */}
      <section className="sec sec-2" data-testid="section-prepare">
        <div className="wrap">
          <div className="label rv rv-up"><span className="idx">04</span>&nbsp;The first product</div>
          <h2 className="disp sec-h rv rv-up">Prepare<span className="g">.</span></h2>
          <p className="serif-line rv rv-up" style={{ marginTop: "3vh", fontSize: "clamp(20px, 2.6vw, 27px)", maxWidth: 640 }}>
            Help the golfer arrive before the round gets away from them.
          </p>
          <div className="body-copy rv rv-up" style={{ maxWidth: 640, marginTop: "4vh" }}>
            <p>Prepare is the first Dialed In product. A premium daily gummy being developed to support a calmer, steadier baseline.</p>
            <p><strong>Not a stimulant. Not a pre-workout. Not a miracle taken twenty minutes before the first tee.</strong></p>
            <p>Prepare is for the golfer who wants less of the day still running through them when the round begins. Because the pressure they carry onto the course did not start in the car park.</p>
            <p><strong>Life happens every day. Prepare is taken every day.</strong></p>
          </div>
          <div className="rv rv-up" style={{ marginTop: "5vh" }}>
            <Link className="cta" to="/prepare#waitlist" data-testid="home-prepare-waitlist-cta">
              Join the Prepare Waitlist <Arrow />
            </Link>
          </div>
        </div>
      </section>

      {/* FOUR PRODUCTS */}
      <section className="sec" data-testid="section-four-products">
        <div className="wrap">
          <div className="label rv rv-up"><span className="idx">05</span>&nbsp;The system</div>
          <h2 className="disp sec-h rv rv-up">This doesn't stop with <span className="g">one product.</span></h2>
          <p className="body-copy rv rv-up" style={{ maxWidth: 640, marginTop: "3vh" }}>
            Dialed In is being built around four moments golfers already recognise.
          </p>

          <div className="moments">
            {MOMENTS.map((m) => (
              <div className="moment-row rv rv-up" key={m.key} data-testid={`moment-${m.key}`}>
                <div className="moment-name"><span className="moment-dot" style={{ background: m.color }} />{m.name}</div>
                <div>
                  <div className="moment-when">{m.when}</div>
                  <p className="moment-copy">{m.copy}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="serif-line rv rv-up" style={{ marginTop: "6vh", fontSize: "clamp(19px, 2.3vw, 24px)" }}>
            Prepare. Reset. Sustain. Recover. Four products. Four clear jobs. One brand built around the whole experience.
          </p>
        </div>
      </section>

      {/* GOLF IS NEVER ONLY ABOUT GOLF */}
      <section className="band" data-testid="section-never-only-golf">
        <div className="band-bg" data-plx="0.14" style={{ backgroundImage: `url('${STORY_IMG}')` }} />
        <div className="band-veil" />
        <div className="band-inner wrap">
          <div className="label rv rv-up"><span className="idx">06</span>&nbsp;The stories</div>
          <h2 className="disp sec-h rv rv-up">Golf is never only <span className="g">about golf.</span></h2>
          <div className="body-copy rv rv-up" style={{ maxWidth: 620, marginTop: "4vh" }}>
            <p>You ask someone why they play, and eventually they tell you about their life.</p>
            <p>A parent and child who found something they could share. A golfer who plays alone at sunrise because it is the only time in the week that is theirs. Someone trying to escape work for four hours. Someone who kept playing through grief because the course gave them somewhere to go.</p>
            <p>And someone who nearly walked off after losing six balls but stayed because this was the only time they had for themselves.</p>
            <p><strong>That's why I love these conversations. They start with golf. Then the real story appears.</strong></p>
            <p>Dialed In is creating somewhere for those stories to exist. Not forced. Not therapy dressed up in golf clothes. Just a brand that understands there is often more behind a round than anyone else can see.</p>
          </div>
        </div>
      </section>

      {/* THE NOD */}
      <section className="sec" data-testid="section-nod">
        <div className="wrap" style={{ textAlign: "center", maxWidth: 780 }}>
          <div className="label rv rv-up" style={{ justifyContent: "center" }}><span className="idx">07</span>&nbsp;The nod</div>
          <h2 className="disp sec-h rv rv-up" style={{ margin: "3.5vh auto 0" }}>One day, two golfers will see the <span className="g">Dialed In logo.</span></h2>
          <div className="verse rv rv-up" style={{ margin: "5vh auto 0", maxWidth: 560 }}>
            <p>One of them will give the other a small nod.</p>
            <p>Nothing dramatic. Nothing needs explaining. Just:</p>
            <p><strong>I get it. I know why you play. I know those few hours matter. I know there may be more behind your round than the scorecard shows.</strong></p>
          </div>
          <p className="serif-line rv rv-up" style={{ marginTop: "5vh", fontSize: "clamp(21px, 2.8vw, 30px)" }}>
            That's what this brand has to earn. Not attention. Recognition.
          </p>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="sec sec-2" data-testid="section-founder">
        <div className="wrap">
          <div className="founder-grid">
            <div>
              <div className="label rv rv-up"><span className="idx">08</span>&nbsp;From the founder</div>
              <div className="founder-photo rv rv-up" style={{ marginTop: "4vh" }}>
                <img src="/images/founder.jpg" alt="Jason Brown, founder of Dialed In" loading="lazy" />
              </div>
            </div>
            <div className="founder-letter rv rv-up">
              <p><span className="hl">I'm building the brand I wish had existed for me.</span></p>
              <p>Golf found me at seventeen, at a point in my life when I badly needed something to hold onto. I was terrible at it. I hated plenty of it. I kept coming back anyway.</p>
              <p>More than twenty years later, I understand why.</p>
              <p>Golf gave me somewhere to put my attention, somewhere to compete, somewhere to connect, somewhere to disappear into for a while, somewhere I could feel like myself again, even before I understood why I didn't always feel that way elsewhere.</p>
              <p>Since then, I've spent years trying to understand stress, pressure, ADHD, the brain, the body and the things people carry without always knowing how much they affect them.</p>
              <p><span className="hl">Dialed In is where all of that meets golf.</span></p>
              <p>Not as a personal project. As a real company. A real product system. A real brand for golfers who know the game gives them more than a score.</p>
              <span className="founder-sig">Jason Brown<br /><span style={{ color: "var(--cream-45)" }}>Founder, Dialed In</span></span>
            </div>
          </div>
        </div>
      </section>

      {/* YOU ARE EARLY + JOIN */}
      <section className="sec" id="join" data-testid="section-join">
        <div className="wrap">
          <div className="join-grid">
            <div>
              <div className="label rv rv-up"><span className="idx">09</span>&nbsp;Join the build</div>
              <h2 className="disp join-h rv rv-up">You are <span className="g">early.</span></h2>
              <div className="body-copy rv rv-up" style={{ maxWidth: 480, marginTop: "3vh" }}>
                <p>Prepare hasn't launched yet. Reset, Sustain and Recover are still ahead of us. The first boxes haven't shipped. The first customers haven't opened them. The first stranger hasn't seen the logo and given someone the nod.</p>
                <p><strong>That's the opportunity.</strong></p>
                <p>You are not finding Dialed In after the story has already been written. You are here while we build it.</p>
              </div>
              <div className="join-links rv rv-up">
                <Link className="text-link" to="/prepare#waitlist" data-testid="join-prepare-link">Join the Prepare Waitlist</Link>
                <a className="text-link" href={JOTFORM_URL} target="_blank" rel="noopener noreferrer" data-testid="join-research-link">Share Your Golf Experience</a>
              </div>
            </div>
            <div className="rv rv-up">
              <JoinForm
                idPrefix="join"
                eventName="join_signup"
                consent="Join Dialed In and receive The Science · Weekly, golfer research, product-development updates and early access. Unsubscribe at any time."
              />
            </div>
          </div>
        </div>
      </section>

      {/* FINAL STATEMENT */}
      <section className="close-band" data-testid="section-close">
        <div className="band-bg" data-plx="0.12" style={{ backgroundImage: `url('${CLOSE_IMG}')` }} />
        <div className="band-veil" />
        <div className="close-inner">
          <img className="close-mark rv rv-up" src="/brand/mark-t.png" alt="" aria-hidden="true" />
          <h2 className="disp close-h rv rv-up" data-testid="close-statement">
            Golf gives people something <span className="g">worth protecting.</span>
          </h2>
          <div className="close-sub rv rv-up">Dialed In &middot; Products for the person carrying the bag</div>
        </div>
      </section>
    </>
  );
}
